import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Clock, Pencil, Trash2, Percent, Eye, RefreshCw, ArrowUpCircle, Users } from "lucide-react";
import { format } from "date-fns";

const planLabels: Record<string, string> = { free: "مجاني", basic: "أساسي", premium: "متقدم", enterprise: "مؤسسي" };
const cycleLabels: Record<string, string> = { monthly: "شهري", yearly: "سنوي" };
const statusLabels: Record<string, string> = { active: "نشط", expired: "منتهي", suspended: "معلق", cancelled: "ملغي" };
const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  expired: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  suspended: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  cancelled: "bg-muted text-muted-foreground",
  trial: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
};

interface Props {
  subscriptions: any[];
  isLoading: boolean;
  onStatusChange: (id: string, status: string) => void;
  onEdit: (sub: any) => void;
  onDelete: (id: string) => void;
  onViewDetails: (sub: any) => void;
  onRenew: (sub: any) => void;
}

const SubscriptionTable = ({ subscriptions, isLoading, onStatusChange, onEdit, onDelete, onViewDetails, onRenew }: Props) => {
  if (isLoading) return <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p>;

  const isExpiringSoon = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    const diff = new Date(expiresAt).getTime() - Date.now();
    return diff > 0 && diff < 30 * 24 * 60 * 60 * 1000;
  };

  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>الحضانة</TableHead>
            <TableHead>النوع</TableHead>
            <TableHead>الخطة</TableHead>
            <TableHead>التسعير</TableHead>
            <TableHead>المبلغ</TableHead>
            <TableHead>الخصم</TableHead>
            <TableHead>الدورة</TableHead>
            <TableHead>الحالة</TableHead>
            <TableHead>تاريخ البدء</TableHead>
            <TableHead>الانتهاء</TableHead>
            <TableHead>الدفع</TableHead>
            <TableHead>إجراءات</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {subscriptions.map((sub) => {
            const isTrial = (sub as any).is_trial;
            const trialEnds = (sub as any).trial_ends_at;
            const discountPct = (sub as any).discount_percent || 0;
            const discountType = (sub as any).discount_type || "none";
            const expiringSoon = isExpiringSoon(sub.expires_at);
            const isExpired = sub.expires_at && new Date(sub.expires_at) < new Date();
            const paymentMethod = sub.payment_method;

            return (
              <TableRow key={sub.id} className={expiringSoon ? "bg-yellow-50/50 dark:bg-yellow-950/10" : isExpired && sub.status === "active" ? "bg-red-50/50 dark:bg-red-950/10" : ""}>
                <TableCell className="font-medium max-w-[150px] truncate">
                  {(sub as any).nurseries?.name || "—"}
                </TableCell>
                <TableCell>
                  {isTrial ? (
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 text-[10px]">
                      <Clock className="w-3 h-3 ml-1" />تجربة
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">مدفوع</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-[10px]">{planLabels[sub.plan] || sub.plan}</Badge>
                </TableCell>
                <TableCell>
                  {(sub as any).pricing_model === "per_child" ? (
                    <Badge variant="secondary" className="text-[10px] gap-0.5">
                      <Users className="w-3 h-3" />بالطفل
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">ثابت</Badge>
                  )}
                </TableCell>
                <TableCell className="font-semibold">
                  {isTrial ? <span className="text-muted-foreground text-xs">مجاني</span> : (
                    <div>
                      <span>{sub.amount.toLocaleString()} ج.م</span>
                      {(sub as any).pricing_model === "per_child" && (sub as any).price_per_child && (
                        <p className="text-[9px] text-muted-foreground">{(sub as any).price_per_child} ج.م/طفل</p>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {discountPct > 0 ? (
                    <span className="text-green-600 text-xs font-medium flex items-center gap-0.5">
                      <Percent className="w-3 h-3" />
                      {discountPct}%
                      <span className="text-[9px] text-muted-foreground">
                        {discountType === "annual" ? "سنوي" : discountType === "group" ? "مجموعة" : discountType === "promo" ? "ترويجي" : ""}
                      </span>
                    </span>
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </TableCell>
                <TableCell className="text-xs">{cycleLabels[sub.billing_cycle] || sub.billing_cycle}</TableCell>
                <TableCell>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${isTrial ? statusColors.trial : (statusColors[sub.status] || "")}`}>
                    {isTrial ? "تجربة" : (statusLabels[sub.status] || sub.status)}
                  </span>
                </TableCell>
                <TableCell className="text-xs">{format(new Date(sub.started_at), "dd/MM/yyyy")}</TableCell>
                <TableCell>
                  {isTrial && trialEnds ? (
                    <span className="text-blue-600 text-xs">{format(new Date(trialEnds), "dd/MM/yyyy")}</span>
                  ) : sub.expires_at ? (
                    <span className={`text-xs ${isExpired ? "text-destructive font-bold" : expiringSoon ? "text-yellow-600 font-medium" : ""}`}>
                      {format(new Date(sub.expires_at), "dd/MM/yyyy")}
                      {expiringSoon && " ⚠️"}
                    </span>
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </TableCell>
                <TableCell className="text-[10px]">
                  {paymentMethod === "bank_transfer" ? "تحويل بنكي" :
                   paymentMethod === "cash" ? "نقدي" :
                   paymentMethod === "online" ? "إلكتروني" :
                   paymentMethod === "card" ? "بطاقة" :
                   paymentMethod === "wallet" ? "محفظة" : "—"}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-0.5">
                    <Select value={sub.status} onValueChange={(val) => onStatusChange(sub.id, val)}>
                      <SelectTrigger className="w-20 h-7 text-[10px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">نشط</SelectItem>
                        <SelectItem value="suspended">معلق</SelectItem>
                        <SelectItem value="expired">منتهي</SelectItem>
                        <SelectItem value="cancelled">ملغي</SelectItem>
                      </SelectContent>
                    </Select>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onViewDetails(sub)}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>عرض التفاصيل</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(sub)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>تعديل</TooltipContent>
                    </Tooltip>
                    {(isExpired || expiringSoon) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => onRenew(sub)}>
                            <RefreshCw className="w-3.5 h-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>تجديد</TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => onDelete(sub.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>حذف</TooltipContent>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {!subscriptions.length && (
            <TableRow>
              <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">لا توجد اشتراكات</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
};

export default SubscriptionTable;
